import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import { privacy, PRIV_COLOR } from '../../lib/privacyApi';
import {
  ShieldCheck, Database, FileText, Inbox, Clock, Lock, Siren, Cpu, Settings2, Plus, X, Check,
} from 'lucide-react';

const Badge = ({ s }) => (
  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${PRIV_COLOR[s] || 'bg-slate-100 text-slate-700'}`}>
    {String(s || '').replace(/_/g, ' ')}
  </span>
);
const ic = 'w-full px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500';
const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors';

const Modal = ({ title, onClose, children, testId }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid={testId}>
    <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between px-5 py-4 border-b">
        <h3 className="text-lg font-semibold">{title}</h3>
        <button onClick={onClose} data-testid="privacy-modal-close" className="p-1.5 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
      </div>
      <div className="p-5 space-y-3">{children}</div>
    </div>
  </div>
);

const Note = ({ children, testId }) => (
  <div className="mb-4 text-xs text-indigo-800 bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2" data-testid={testId}>{children}</div>
);

const Card = ({ label, value, testId }) => (
  <div className="bg-white rounded-xl border border-slate-100 p-4" data-testid={testId}>
    <div className="text-2xl font-bold text-slate-900">{value ?? '—'}</div>
    <div className="text-xs text-slate-500 mt-1">{label}</div>
  </div>
);

// ------------------------------------------------------------------ Overview
function Overview() {
  const [d, setD] = useState(null);
  useEffect(() => { privacy.overview().then(setD).catch(() => toast.error('Load failed')); }, []);
  if (!d) return <div className="text-slate-400 text-sm" data-testid="privacy-overview-loading">Loading…</div>;
  return (
    <div data-testid="privacy-overview">
      <Note testId="privacy-governance-note">
        These are technical privacy &amp; data-protection controls that <strong>support</strong> organizational
        compliance obligations. They do <strong>not</strong> constitute DPDP/GDPR/ISO certification or legal
        compliance. Factual counts only — no compliance scores are produced.
      </Note>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card label="Open Privacy Requests" value={d.openPrivacyRequests} testId="ov-open-requests" />
        <Card label="Awaiting Identity Verification" value={d.requestsAwaitingVerification} testId="ov-awaiting-verify" />
        <Card label="Retention Actions Pending Review" value={d.retentionActionsPending} testId="ov-retention-pending" />
        <Card label="Active Legal/Project Holds" value={d.activeHolds} testId="ov-active-holds" />
        <Card label="Open Security/Privacy Incidents" value={d.openSecurityIncidents} testId="ov-open-incidents" />
        <Card label="Published Privacy Notice" value={d.publishedPrivacyNotice || 'None'} testId="ov-published-notice" />
        <Card label="AI Providers Enabled" value={d.aiProvidersEnabled} testId="ov-ai-enabled" />
        <Card label="Data Inventory Entries" value={d.dataInventoryEntries} testId="ov-inventory" />
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ Data Inventory
function Inventory() {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => privacy.listInventory().then(setRows).catch(() => toast.error('Load failed')), []);
  useEffect(() => { load(); }, [load]);
  const seed = async () => { try { const r = await privacy.seedInventory(); toast.success(`Seeded ${r.seeded}`); load(); } catch { toast.error('Seed failed'); } };
  return (
    <div data-testid="privacy-inventory-tab">
      <Note>Personal Data ≠ All Project Data. This registry records only data categories and their actual processing — the technical source of truth for privacy engineering.</Note>
      <div className="flex justify-end mb-3">
        <button onClick={seed} className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700`} data-testid="inventory-seed-btn"><Plus className="w-4 h-4" /> Seed from actual data flows</button>
      </div>
      <div className="bg-white rounded-xl border border-slate-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>
            <th className="text-left px-4 py-2.5">Category</th><th className="text-left px-4 py-2.5">Collection</th>
            <th className="text-left px-4 py-2.5">Class</th><th className="text-left px-4 py-2.5">AI</th>
            <th className="text-left px-4 py-2.5">Deletion</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-50" data-testid={`inventory-row-${r.id}`}>
                <td className="px-4 py-2.5 text-slate-700">{r.category}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{r.collection}</td>
                <td className="px-4 py-2.5"><Badge s={r.dataClass} /></td>
                <td className="px-4 py-2.5">{r.aiProcessing ? 'Yes' : 'No'}</td>
                <td className="px-4 py-2.5 text-xs text-slate-500">{r.deletionBehavior}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400" data-testid="inventory-empty">No entries — click “Seed”.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ Notices
function Notices() {
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({});
  const load = useCallback(() => privacy.listNotices().then(setRows).catch(() => toast.error('Load failed')), []);
  useEffect(() => { load(); }, [load]);
  const create = async () => { try { await privacy.createNotice(form); toast.success('Draft created'); setShow(false); setForm({}); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } };
  const move = async (id, target) => { try { await privacy.transitionNotice(id, target); toast.success(target); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } };
  return (
    <div data-testid="privacy-notices-tab">
      <Note>Published notices are immutable; superseded versions are retained so evidence of the notice presented to a user remains available.</Note>
      <div className="flex justify-end mb-3"><button onClick={() => setShow(true)} className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700`} data-testid="notice-new-btn"><Plus className="w-4 h-4" /> New notice</button></div>
      <div className="bg-white rounded-xl border border-slate-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="text-left px-4 py-2.5">Version</th><th className="text-left px-4 py-2.5">Title</th><th className="text-left px-4 py-2.5">Status</th><th className="text-left px-4 py-2.5">Actions</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-50" data-testid={`notice-row-${r.id}`}>
                <td className="px-4 py-2.5 font-mono text-xs text-indigo-800">{r.version}</td>
                <td className="px-4 py-2.5 text-slate-700">{r.title}</td>
                <td className="px-4 py-2.5"><Badge s={r.status} /></td>
                <td className="px-4 py-2.5 space-x-2">
                  {r.status === 'DRAFT' && <button onClick={() => move(r.id, 'REVIEW')} className="text-xs text-amber-700 hover:underline" data-testid={`notice-review-${r.id}`}>Send to review</button>}
                  {r.status === 'REVIEW' && <button onClick={() => move(r.id, 'PUBLISHED')} className="text-xs text-green-700 hover:underline" data-testid={`notice-publish-${r.id}`}>Publish</button>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-400" data-testid="notices-empty">No notices yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {show && (
        <Modal title="New Privacy Notice" onClose={() => setShow(false)} testId="notice-create-modal">
          <input placeholder="Title" className={ic} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="notice-title-input" />
          <input placeholder="Version (e.g. v1)" className={ic} onChange={(e) => setForm({ ...form, version: e.target.value })} data-testid="notice-version-input" />
          <textarea placeholder="Summary" className={ic} rows={3} onChange={(e) => setForm({ ...form, summary: e.target.value })} data-testid="notice-summary-input" />
          <p className="text-xs text-slate-400">[LEGAL REVIEW REQUIRED] Draft content should be reviewed by legal/organizational stakeholders before publishing.</p>
          <button onClick={create} className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700 w-full justify-center`} data-testid="notice-create-submit">Create draft</button>
        </Modal>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ Requests
function Requests() {
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const [reason, setReason] = useState('');
  const load = useCallback(() => privacy.listRequests().then(setRows).catch(() => toast.error('Load failed')), []);
  useEffect(() => { load(); }, [load]);
  const act = async (fn, label) => { try { const r = await fn(); toast.success(label); setDetail(r); load(); } catch (e) { toast.error(e?.response?.data?.detail || `${label} failed`); } };
  return (
    <div data-testid="privacy-requests-tab">
      <Note>Deletion is never an immediate hard delete. Rejection/partial fulfilment require a reason. Identity is verified before fulfilment. Deadlines are configurable, not fabricated statutory deadlines.</Note>
      <div className="bg-white rounded-xl border border-slate-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="text-left px-4 py-2.5">Number</th><th className="text-left px-4 py-2.5">Type</th><th className="text-left px-4 py-2.5">Verified</th><th className="text-left px-4 py-2.5">Status</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} onClick={() => { setDetail(r); setReason(''); }} className="border-t border-slate-50 hover:bg-indigo-50/40 cursor-pointer" data-testid={`request-row-${r.id}`}>
                <td className="px-4 py-2.5 font-mono text-xs text-indigo-800">{r.number}</td>
                <td className="px-4 py-2.5 text-slate-700">{r.requestType}</td>
                <td className="px-4 py-2.5">{r.identityVerified ? <Check className="w-4 h-4 text-green-600" /> : '—'}</td>
                <td className="px-4 py-2.5"><Badge s={r.status} /></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-400" data-testid="requests-empty">No requests yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {detail && (
        <Modal title={`${detail.number} · ${detail.requestType}`} onClose={() => setDetail(null)} testId="request-detail-modal">
          <div className="text-sm text-slate-600"><Badge s={detail.status} /> · Target: {String(detail.targetDate || '').slice(0, 10)}</div>
          {detail.deletionAnalysis && (
            <div className="text-xs bg-amber-50 border border-amber-100 rounded-lg p-3 text-amber-800" data-testid="request-deletion-analysis">
              <strong>Governed outcome:</strong> {detail.deletionAnalysis.recommendedOutcome.replace(/_/g, ' ')}<br />
              {detail.deletionAnalysis.explanation}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {!detail.identityVerified && <button onClick={() => act(() => privacy.verifyIdentity(detail.id, true), 'Identity verified')} className={`${btn} bg-sky-600 text-white hover:bg-sky-700`} data-testid="request-verify-btn">Verify identity</button>}
            {['RECEIVED', 'IDENTITY_VERIFICATION'].includes(detail.status) && <button onClick={() => act(() => privacy.transitionRequest(detail.id, { target: 'UNDER_REVIEW' }), 'Under review')} className={`${btn} bg-amber-600 text-white hover:bg-amber-700`} data-testid="request-review-btn">Start review</button>}
            {['UNDER_REVIEW', 'ACTION_REQUIRED'].includes(detail.status) && <button onClick={() => act(() => privacy.transitionRequest(detail.id, { target: 'FULFILLED', evidence: { note: 'fulfilled via center' } }), 'Fulfilled')} className={`${btn} bg-green-600 text-white hover:bg-green-700`} data-testid="request-fulfill-btn">Fulfil</button>}
            {detail.requestType === 'ACCOUNT_DELETION' && detail.status !== 'FULFILLED' && <button onClick={() => act(() => privacy.executeDeletion(detail.id), 'Deletion executed')} className={`${btn} bg-rose-600 text-white hover:bg-rose-700`} data-testid="request-execute-deletion-btn">Execute governed deletion</button>}
          </div>
          {['UNDER_REVIEW', 'ACTION_REQUIRED'].includes(detail.status) && (
            <div className="pt-2 border-t">
              <input placeholder="Reason (required to reject)" className={ic} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="request-reject-reason" />
              <button onClick={() => act(() => privacy.transitionRequest(detail.id, { target: 'REJECTED', reason }), 'Rejected')} className={`${btn} bg-slate-600 text-white hover:bg-slate-700 mt-2`} data-testid="request-reject-btn">Reject with reason</button>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ Retention + Holds
function Retention() {
  const [policies, setPolicies] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ trigger: 'CREATED_DATE', action: 'REVIEW', retentionPeriodDays: 365 });
  const load = useCallback(() => privacy.listPolicies().then(setPolicies).catch(() => toast.error('Load failed')), []);
  useEffect(() => { load(); }, [load]);
  const create = async () => { try { await privacy.createPolicy(form); toast.success('Policy created'); setShow(false); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } };
  const dry = async (id) => { try { const r = await privacy.dryRun(id); toast.success(`Dry-run: ${r.eligibleCount} eligible, ${r.blockedByHoldCount} held`); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } };
  const exec = async (id) => { try { const r = await privacy.executePolicy(id); toast.success(`Processed ${r.processed}, blocked ${r.blockedByHold}`); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } };
  return (
    <div data-testid="privacy-retention-tab">
      <Note>No hard-coded statutory periods. Always dry-run first. Holds always win — Retention Expiry ≠ Permission to Destroy Held Records. Hard DELETE is only allowed on safe non-authoritative collections.</Note>
      <div className="flex justify-end mb-3"><button onClick={() => setShow(true)} className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700`} data-testid="policy-new-btn"><Plus className="w-4 h-4" /> New policy</button></div>
      <div className="bg-white rounded-xl border border-slate-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="text-left px-4 py-2.5">Name</th><th className="text-left px-4 py-2.5">Collection</th><th className="text-left px-4 py-2.5">Action</th><th className="text-left px-4 py-2.5">Days</th><th className="text-left px-4 py-2.5"> </th></tr></thead>
          <tbody>
            {policies.map((p) => (
              <tr key={p.id} className="border-t border-slate-50" data-testid={`policy-row-${p.id}`}>
                <td className="px-4 py-2.5 text-slate-700">{p.name}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{p.collection}</td>
                <td className="px-4 py-2.5"><Badge s={p.action} /></td>
                <td className="px-4 py-2.5">{p.retentionPeriodDays}</td>
                <td className="px-4 py-2.5 space-x-2">
                  <button onClick={() => dry(p.id)} className="text-xs text-sky-700 hover:underline" data-testid={`policy-dryrun-${p.id}`}>Dry-run</button>
                  <button onClick={() => exec(p.id)} className="text-xs text-rose-700 hover:underline" data-testid={`policy-exec-${p.id}`}>Execute</button>
                </td>
              </tr>
            ))}
            {policies.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400" data-testid="policies-empty">No policies yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {show && (
        <Modal title="New Retention Policy" onClose={() => setShow(false)} testId="policy-create-modal">
          <input placeholder="Name" className={ic} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="policy-name-input" />
          <input placeholder="Collection (e.g. notifications)" className={ic} onChange={(e) => setForm({ ...form, collection: e.target.value })} data-testid="policy-collection-input" />
          <select className={ic} value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })} data-testid="policy-action-input">
            {['REVIEW', 'DE_IDENTIFY', 'ANONYMIZE', 'ARCHIVE', 'DELETE'].map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <input type="number" placeholder="Retention days" className={ic} value={form.retentionPeriodDays} onChange={(e) => setForm({ ...form, retentionPeriodDays: Number(e.target.value) })} data-testid="policy-days-input" />
          <button onClick={create} className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700 w-full justify-center`} data-testid="policy-create-submit">Create</button>
        </Modal>
      )}
    </div>
  );
}

function Holds() {
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ scope: { all: true } });
  const load = useCallback(() => privacy.listHolds().then(setRows).catch(() => toast.error('Load failed')), []);
  useEffect(() => { load(); }, [load]);
  const create = async () => { try { await privacy.createHold(form); toast.success('Hold created'); setShow(false); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } };
  const release = async (id) => { try { await privacy.releaseHold(id); toast.success('Released'); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } };
  return (
    <div data-testid="privacy-holds-tab">
      <Note>A legal/project hold prevents scheduled deletion/anonymization for the records it covers. It is a technical preservation control (not legally sufficient by itself).</Note>
      <div className="flex justify-end mb-3"><button onClick={() => setShow(true)} className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700`} data-testid="hold-new-btn"><Plus className="w-4 h-4" /> New hold</button></div>
      <div className="bg-white rounded-xl border border-slate-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="text-left px-4 py-2.5">Number</th><th className="text-left px-4 py-2.5">Reason</th><th className="text-left px-4 py-2.5">Status</th><th className="text-left px-4 py-2.5"> </th></tr></thead>
          <tbody>
            {rows.map((h) => (
              <tr key={h.id} className="border-t border-slate-50" data-testid={`hold-row-${h.id}`}>
                <td className="px-4 py-2.5 font-mono text-xs text-indigo-800">{h.number}</td>
                <td className="px-4 py-2.5 text-slate-700">{h.reason}</td>
                <td className="px-4 py-2.5"><Badge s={h.status} /></td>
                <td className="px-4 py-2.5">{h.status === 'ACTIVE' && <button onClick={() => release(h.id)} className="text-xs text-slate-700 hover:underline" data-testid={`hold-release-${h.id}`}>Release</button>}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-400" data-testid="holds-empty">No holds.</td></tr>}
          </tbody>
        </table>
      </div>
      {show && (
        <Modal title="New Legal/Project Hold" onClose={() => setShow(false)} testId="hold-create-modal">
          <input placeholder="Reason" className={ic} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="hold-reason-input" />
          <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" defaultChecked onChange={(e) => setForm({ ...form, scope: e.target.checked ? { all: true } : {} })} data-testid="hold-scope-all" /> Apply to all tenant records</label>
          <button onClick={create} className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700 w-full justify-center`} data-testid="hold-create-submit">Create hold</button>
        </Modal>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ Security Incidents
function SecurityIncidents() {
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({});
  const [rationale, setRationale] = useState('');
  const load = useCallback(() => privacy.listSecIncidents().then(setRows).catch(() => toast.error('Load failed')), []);
  useEffect(() => { load(); }, [load]);
  const create = async () => { try { await privacy.createSecIncident(form); toast.success('Incident logged'); setShow(false); setForm({}); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } };
  const move = async (target) => { try { const r = await privacy.transitionSecIncident(detail.id, { target }); setDetail(r); toast.success(target); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } };
  const classify = async (cls) => { try { const r = await privacy.classifySecIncident(detail.id, { classification: cls, rationale }); setDetail(r); toast.success('Classified'); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } };
  const nexts = { REPORTED: 'TRIAGED', TRIAGED: 'INVESTIGATING', INVESTIGATING: 'CONTAINED', CONTAINED: 'ASSESSED', ASSESSED: 'REMEDIATED', REMEDIATED: 'CLOSED' };
  return (
    <div data-testid="privacy-incidents-tab">
      <Note testId="sec-inc-governance-note">This is an Information Security / Personal Data Breach register — distinct from a construction Safety Incident. Whether an incident is a legally reportable personal-data breach is a human/legal determination, never auto-derived. [LEGAL REVIEW REQUIRED]</Note>
      <div className="flex justify-end mb-3"><button onClick={() => setShow(true)} className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700`} data-testid="secinc-new-btn"><Plus className="w-4 h-4" /> Log incident</button></div>
      <div className="bg-white rounded-xl border border-slate-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="text-left px-4 py-2.5">Number</th><th className="text-left px-4 py-2.5">Title</th><th className="text-left px-4 py-2.5">Status</th><th className="text-left px-4 py-2.5">Reportability</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} onClick={() => { setDetail(r); setRationale(''); }} className="border-t border-slate-50 hover:bg-indigo-50/40 cursor-pointer" data-testid={`secinc-row-${r.id}`}>
                <td className="px-4 py-2.5 font-mono text-xs text-indigo-800">{r.number}</td>
                <td className="px-4 py-2.5 text-slate-700">{r.title}</td>
                <td className="px-4 py-2.5"><Badge s={r.status} /></td>
                <td className="px-4 py-2.5"><Badge s={r.reportability?.classification} /></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-400" data-testid="secinc-empty">No incidents.</td></tr>}
          </tbody>
        </table>
      </div>
      {show && (
        <Modal title="Log Security/Privacy Incident" onClose={() => setShow(false)} testId="secinc-create-modal">
          <input placeholder="Title" className={ic} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="secinc-title-input" />
          <textarea placeholder="Scope / description" className={ic} rows={3} onChange={(e) => setForm({ ...form, scope: e.target.value })} data-testid="secinc-scope-input" />
          <button onClick={create} className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700 w-full justify-center`} data-testid="secinc-create-submit">Log</button>
        </Modal>
      )}
      {detail && (
        <Modal title={`${detail.number} · ${detail.title}`} onClose={() => setDetail(null)} testId="secinc-detail-modal">
          <div className="text-sm"><Badge s={detail.status} /> · Reportability: <Badge s={detail.reportability?.classification} /></div>
          {nexts[detail.status] && <button onClick={() => move(nexts[detail.status])} className={`${btn} bg-amber-600 text-white hover:bg-amber-700`} data-testid="secinc-advance-btn">Advance → {nexts[detail.status]}</button>}
          <div className="pt-2 border-t">
            <p className="text-xs text-slate-500 mb-2">Reportability is a human classification. [LEGAL REVIEW REQUIRED]</p>
            <input placeholder="Rationale (required)" className={ic} value={rationale} onChange={(e) => setRationale(e.target.value)} data-testid="secinc-rationale-input" />
            <div className="flex gap-2 mt-2">
              <button onClick={() => classify('REPORTABLE')} className={`${btn} bg-rose-600 text-white hover:bg-rose-700`} data-testid="secinc-reportable-btn">Reportable</button>
              <button onClick={() => classify('NOT_REPORTABLE')} className={`${btn} bg-slate-600 text-white hover:bg-slate-700`} data-testid="secinc-notreportable-btn">Not reportable</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ AI & External Processing
function AiGovernance() {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => privacy.listAiProviders().then(setRows).catch(() => toast.error('Load failed')), []);
  useEffect(() => { load(); }, [load]);
  const seed = async () => { try { await privacy.seedAiProviders(); toast.success('Seeded'); load(); } catch { toast.error('Seed failed'); } };
  const toggle = async (id, enabled) => { try { await privacy.toggleAiProvider(id, enabled); toast.success(enabled ? 'Enabled' : 'Disabled'); load(); } catch { toast.error('Failed'); } };
  return (
    <div data-testid="privacy-ai-tab">
      <Note>Documents actual external AI data flows. Existing governance is preserved: Evidence → AI Proposal → Human Review → Authoritative Record. Sensitive fields are stripped before transmission (data minimization). AI Provider ≠ automatically privacy-safe.</Note>
      <div className="flex justify-end mb-3"><button onClick={seed} className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700`} data-testid="ai-seed-btn"><Plus className="w-4 h-4" /> Seed providers</button></div>
      <div className="bg-white rounded-xl border border-slate-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="text-left px-4 py-2.5">Provider</th><th className="text-left px-4 py-2.5">Model</th><th className="text-left px-4 py-2.5">External</th><th className="text-left px-4 py-2.5">Enabled</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-50" data-testid={`ai-row-${r.id}`}>
                <td className="px-4 py-2.5 text-slate-700">{r.provider}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{r.model}</td>
                <td className="px-4 py-2.5">{r.externalProcessing ? 'Yes' : 'No'}</td>
                <td className="px-4 py-2.5"><button onClick={() => toggle(r.id, !r.enabled)} className={`text-xs hover:underline ${r.enabled ? 'text-green-700' : 'text-slate-500'}`} data-testid={`ai-toggle-${r.id}`}>{r.enabled ? 'Enabled' : 'Disabled'}</button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-400" data-testid="ai-empty">No providers — click “Seed”.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ Configuration
function Configuration() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { privacy.getConfig().then(setCfg).catch(() => toast.error('Load failed')); }, []);
  const save = async () => { try { const r = await privacy.updateConfig(cfg); setCfg(r); toast.success('Saved'); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } };
  if (!cfg) return <div className="text-slate-400 text-sm">Loading…</div>;
  return (
    <div data-testid="privacy-config-tab" className="space-y-3 max-w-xl">
      <Note>[LEGAL/CONTRACTUAL CONFIGURATION] The data-fiduciary/processor role model is a technical configuration, not an automatic legal determination. Confirm against your executed agreements. [LEGAL REVIEW REQUIRED]</Note>
      <div className="bg-white rounded-xl border border-slate-100 p-4 space-y-2 text-sm">
        <div className="text-xs text-slate-500 uppercase">Role model</div>
        <div>Platform Provider: <strong>{cfg.roleModel?.platformProvider}</strong></div>
        <div>Tenant / Customer: <strong>{cfg.roleModel?.tenant}</strong></div>
      </div>
      <label className="block text-sm text-slate-600">Privacy contact email
        <input className={ic} value={cfg.privacyContact?.email || ''} onChange={(e) => setCfg({ ...cfg, privacyContact: { ...cfg.privacyContact, email: e.target.value } })} data-testid="config-privacy-email" />
      </label>
      <label className="block text-sm text-slate-600">Grievance contact email
        <input className={ic} value={cfg.grievanceContact?.email || ''} onChange={(e) => setCfg({ ...cfg, grievanceContact: { ...cfg.grievanceContact, email: e.target.value } })} data-testid="config-grievance-email" />
      </label>
      <label className="block text-sm text-slate-600">Jurisdiction
        <input className={ic} value={cfg.jurisdiction || ''} onChange={(e) => setCfg({ ...cfg, jurisdiction: e.target.value })} data-testid="config-jurisdiction" />
      </label>
      <button onClick={save} className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700`} data-testid="config-save-btn">Save configuration</button>
    </div>
  );
}

const TABS = [
  { k: 'overview', label: 'Overview', icon: ShieldCheck, C: Overview },
  { k: 'inventory', label: 'Data Inventory', icon: Database, C: Inventory },
  { k: 'notices', label: 'Privacy Notices', icon: FileText, C: Notices },
  { k: 'requests', label: 'Privacy Requests', icon: Inbox, C: Requests },
  { k: 'retention', label: 'Retention', icon: Clock, C: Retention },
  { k: 'holds', label: 'Holds', icon: Lock, C: Holds },
  { k: 'incidents', label: 'Security Incidents', icon: Siren, C: SecurityIncidents },
  { k: 'ai', label: 'AI & External Processing', icon: Cpu, C: AiGovernance },
  { k: 'config', label: 'Configuration', icon: Settings2, C: Configuration },
];

export default function PrivacyComplianceCenter() {
  const [tab, setTab] = useState('overview');
  const Active = TABS.find((t) => t.k === tab).C;
  return (
    <Layout>
      <div className="space-y-6" data-testid="privacy-compliance-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2"><ShieldCheck className="w-7 h-7 text-indigo-600" /> Privacy &amp; Compliance Center</h1>
          <p className="text-slate-600 mt-1 text-sm">Privacy-by-design controls that support organizational compliance obligations. Not a certification.</p>
        </div>
        <div className="flex gap-1.5 overflow-x-auto border-b border-slate-200 pb-px">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`privacy-tab-${t.k}`}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === t.k ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
        <Active />
      </div>
    </Layout>
  );
}
